/**
 * Freight words and small pure rules for the screens (TASK-0257, DROP-2a). On screen: "Freight", "Drop-off
 * charges", "Backhaul", "Trip", "Cost with freight" (tooltip: landed cost), "est." for an estimate, "billed"
 * for a placed bill (DROP-2b), "confirmed" for our own truck's run cost. Never "landed" or "allocation" alone.
 */
import {
  BASIS_LABEL,
  CATEGORY_LABEL,
  isSharedBasis,
  parseChargeRule,
  perOrderAmount,
  ruleAmountText,
  ruleInForce,
  weekStartOf,
  type ChargeRule as SharedRule,
  type LandedLine,
} from '@kk-pos/shared/landed-cost';
import { addStoreDays } from '@kk-pos/shared/drop-off';
import { money } from '@/lib/format';
import type {
  ChargeCategory,
  ChargeRule,
  ChargeRuleBody,
  ChargeView,
  RuleBasis,
  Trip,
} from './types';

export const COST_WITH_FREIGHT_TIP = 'landed cost: what it cost to get it onto our shelf';

/** `est.`, `billed`, `confirmed`. */
export function statusWord(c: Pick<ChargeView, 'status' | 'source'>): string {
  if (c.status === 'est') return 'est.';
  return c.source === 'run_cost' ? 'confirmed' : 'billed';
}

export function categoryLabel(category: ChargeCategory): string {
  return CATEGORY_LABEL[category];
}

export { BASIS_LABEL, CATEGORY_LABEL, ruleAmountText };

/** Who a rule is for: `Every vendor here`, `Only Acme Foods`, `Acme Foods's own orders`. */
export function ruleForText(
  rule: Pick<ChargeRule, 'locationId' | 'vendorId' | 'vendorName'>
): string {
  if (rule.locationId === null) return 'Its own orders';
  if (rule.vendorId === null) return 'Every vendor here';
  return `Exception: ${rule.vendorName ?? `vendor ${rule.vendorId}`}`;
}

/** The rule's days: `from Feb 17, 2026`, `Jan 1 – Feb 16, 2026`. */
export function ruleDaysText(rule: Pick<ChargeRule, 'startsOn' | 'endsOn'>): string {
  return rule.endsOn
    ? `${dayLabel(rule.startsOn)} – ${dayLabel(rule.endsOn)}`
    : `from ${dayLabel(rule.startsOn)}`;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** `Tue Oct 27` (store day, read as UTC so no zone moves it). */
export function dayLabel(isoDay: string, withWeekday = false): string {
  const d = new Date(`${isoDay}T00:00:00Z`);
  const text = `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  const year =
    d.getUTCFullYear() !== new Date().getUTCFullYear() ? `, ${d.getUTCFullYear()}` : '';
  return withWeekday ? `${WEEKDAYS[d.getUTCDay()]} ${text}${year}` : `${text}${year}`;
}

/** The store week (Sun–Sat) around a day, and the weeks before / after. */
export function weekWindow(day: string, offsetWeeks = 0): { from: string; to: string } {
  const from = addStoreDays(weekStartOf(day), offsetWeeks * 7);
  return { from, to: addStoreDays(from, 6) };
}

export function tripTitle(
  trip: Pick<Trip, 'locationName' | 'arrivesOn' | 'seq'>
): string {
  return `${trip.locationName} → store ${dayLabel(trip.arrivesOn, true)}${trip.seq > 1 ? ` (run ${trip.seq})` : ''}`;
}

export function carriedByText(
  trip: Pick<Trip, 'carriedBy' | 'carrierName' | 'locationName'>
): string | null {
  if (trip.carriedBy === 'own_truck') return 'our truck';
  if (trip.carriedBy === 'carrier')
    return trip.carrierName ? `${trip.carrierName} truck` : 'a carrier';
  if (trip.carriedBy === 'location') return `${trip.locationName}'s truck`;
  return null;
}

/** Today in the browser (the store's own zone), `YYYY-MM-DD`: never the UTC day (review F8). */
export function localToday(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** What a set of trip charges is: `backhaul` / `pallets` for one kind, `trip` for a mix (review F8). */
function tripCostWord(charges: readonly Pick<ChargeView, 'category'>[]): string {
  const kinds = new Set(charges.map((c) => c.category));
  return kinds.size === 1 ? CATEGORY_LABEL[[...kinds][0]!].toLowerCase() : 'trip';
}

/** The trip's own cost on its card: `backhaul $250.00 est.` / `backhaul $200.00 confirmed` / null (no rule). */
export function tripCostText(
  trip: Pick<Trip, 'costEstCents' | 'costActualCents' | 'tripCharges'>
): string | null {
  if (trip.costActualCents !== null) {
    const actuals = trip.tripCharges.filter((c) => c.status === 'actual');
    const run = actuals[0];
    return `${tripCostWord(actuals.length > 0 ? actuals : trip.tripCharges)} ${money(trip.costActualCents)} ${run ? statusWord(run) : 'billed'}`;
  }
  if (trip.tripCharges.length === 0) return null;
  const est = trip.tripCharges.filter((c) => c.status === 'est' && c.counted);
  return `${tripCostWord(est.length > 0 ? est : trip.tripCharges)} ${money(trip.costEstCents)} est.`;
}

/* ── the rule form ─────────────────────────────────────────────────────── */

export interface RuleForm {
  category: ChargeCategory;
  basis: RuleBasis;
  /** Cents (money bases). */
  amountCents: number | null;
  /** Percent text for percent_of_goods (`3`, `2.5`). */
  percentText: string;
  casesPerPalletText: string;
  /** Location rules: '' = every vendor here, else a vendor id (an exception). */
  exceptionVendorId: number | null;
  payeeVendorId: number | null;
  startsOn: string;
  endsOn: string;
  note: string;
  active: boolean;
}

export function ruleFormOf(
  rule: ChargeRule | null,
  today: string,
  defaults: Partial<RuleForm> = {}
): RuleForm {
  if (!rule) {
    return {
      category: 'drop_off',
      basis: 'each_drop',
      amountCents: null,
      percentText: '',
      casesPerPalletText: '',
      exceptionVendorId: null,
      payeeVendorId: null,
      startsOn: today,
      endsOn: '',
      note: '',
      active: true,
      ...defaults,
    };
  }
  return {
    category: rule.category,
    basis: rule.basis,
    amountCents: rule.basis === 'percent_of_goods' ? null : rule.amountCents,
    percentText: rule.basis === 'percent_of_goods' ? String(rule.amountCents / 100) : '',
    casesPerPalletText: rule.casesPerPallet == null ? '' : String(rule.casesPerPallet),
    exceptionVendorId: rule.locationId !== null ? rule.vendorId : null,
    payeeVendorId: rule.payeeVendorId,
    startsOn: rule.startsOn,
    endsOn: rule.endsOn ?? '',
    note: rule.note ?? '',
    active: rule.active,
  };
}

/** The body for an owner (a location, or a vendor alone), worded with the shared validator. */
export function ruleBodyOf(
  form: RuleForm,
  owner: { locationId: number } | { vendorId: number }
): { body: ChargeRuleBody | null; errors: string[] } {
  const percent = form.percentText.trim();
  const bp = /^\d{1,3}(\.\d{1,2})?$/.test(percent)
    ? Math.round(Number(percent) * 100)
    : -1;
  const cpp = form.casesPerPalletText.trim();
  const raw = {
    locationId: 'locationId' in owner ? owner.locationId : null,
    vendorId: 'locationId' in owner ? form.exceptionVendorId : owner.vendorId,
    category: form.category,
    basis: form.basis,
    amountCents: form.basis === 'percent_of_goods' ? bp : (form.amountCents ?? -1),
    casesPerPallet:
      form.basis === 'each_pallet' ? (cpp === '' ? null : Number(cpp)) : null,
    payeeVendorId: form.payeeVendorId,
    startsOn: form.startsOn,
    endsOn: form.endsOn === '' ? null : form.endsOn,
    note: form.note,
    active: form.active,
  };
  const parsed = parseChargeRule(raw);
  if (!parsed.success) return { body: null, errors: parsed.errors };
  return { body: parsed.data, errors: [] };
}

/** The bases a rule form offers: a vendor's own charge and an exception have no shared bases. */
export function basesFor(
  ownerKind: 'location' | 'vendor',
  exception: boolean
): RuleBasis[] {
  const all: RuleBasis[] = [
    'each_drop',
    'each_case',
    'each_pallet',
    'each_trip',
    'weekly',
    'monthly',
    'percent_of_goods',
  ];
  return ownerKind === 'vendor' || exception ? all.filter((b) => !isSharedBasis(b)) : all;
}

/* ── the location's sample line ────────────────────────────────────────── */

function toShared(r: ChargeRule): SharedRule {
  return {
    id: r.id,
    locationId: r.locationId,
    vendorId: r.vendorId,
    category: r.category,
    basis: r.basis,
    amountCents: r.amountCents,
    casesPerPallet: r.casesPerPallet,
    payeeVendorId: r.payeeVendorId,
    startsOn: r.startsOn,
    endsOn: r.endsOn,
    active: r.active,
  };
}

/**
 * `A 30-case drop here costs about $45.00 drop-off + $4.00 pallets + its share of the $250.00 trip`, from the
 * location's own rules in force on `day` (exceptions aside). Null when it has none.
 */
export function locationSampleLine(
  rules: readonly ChargeRule[],
  locationId: number,
  day: string,
  cases = 30
): string | null {
  const shared = rules
    .filter((r) => r.locationId === locationId && r.vendorId === null)
    .map(toShared);
  const kinds = new Map<string, { category: ChargeCategory; basis: RuleBasis }>();
  for (const r of shared)
    kinds.set(`${r.category}:${r.basis}`, { category: r.category, basis: r.basis });
  const line: LandedLine = {
    id: 1,
    poId: 1,
    productId: 1,
    unit: 'ea',
    orderedQty: cases,
    receivedQty: 0,
    packSize: 1,
    shippedCaseCount: null,
    unitCostCents: null,
    costIsEstimate: true,
    lbPerUnit: null,
  };
  const parts: string[] = [];
  for (const k of kinds.values()) {
    const rule = ruleInForce(shared, {
      locationId,
      vendorId: null,
      category: k.category,
      basis: k.basis,
      day,
    });
    if (!rule) continue;
    const what = CATEGORY_LABEL[rule.category].toLowerCase();
    if (rule.basis === 'each_trip')
      parts.push(`its share of the ${money(rule.amountCents)} trip (${what})`);
    else if (rule.basis === 'weekly')
      parts.push(`its share of ${money(rule.amountCents)} a week (${what})`);
    else if (rule.basis === 'monthly')
      parts.push(`its share of ${money(rule.amountCents)} a month (${what})`);
    else if (rule.basis === 'percent_of_goods')
      parts.push(`${ruleAmountText(rule)} (${what})`);
    else
      parts.push(
        `${money(perOrderAmount(rule, { lines: [line], received: false }))} ${what}`
      );
  }
  if (parts.length === 0) return null;
  return `A ${cases}-case drop here costs about ${parts.join(' + ')}`;
}

/* ── Sources ───────────────────────────────────────────────────────────── */

/**
 * When the cheapest vendor by price is not the cheapest once freight is in, the one that is (a quiet note; it
 * never changes the usual source or a quantity). Vendors without a price are left out; a vendor with no
 * freight counts at its price. A vendor whose freight is NOT KNOWN (`freightUnknown`: a charge applies but
 * nothing has been received to estimate it) is never named, and when it is the cheapest by price there is no
 * note at all: we cannot tell whether freight flips it (review R2-2).
 */
export function cheaperWithFreight(
  vendors: readonly {
    name: string;
    priceCents: number | null;
    freightPerUnitCents: number | null;
    freightUnknown?: boolean;
  }[]
): string | null {
  type Priced = {
    name: string;
    priceCents: number;
    freightPerUnitCents: number | null;
    freightUnknown?: boolean;
  };
  const priced = vendors.filter((v): v is Priced => v.priceCents !== null);
  if (priced.length < 2) return null;
  const byPrice = [...priced].sort((a, b) => a.priceCents - b.priceCents)[0]!;
  if (byPrice.freightUnknown) return null;
  const withFreight = (v: Priced) => v.priceCents + (v.freightPerUnitCents ?? 0);
  const known = priced.filter((v) => !v.freightUnknown);
  const best = [...known].sort((a, b) => withFreight(a) - withFreight(b))[0]!;
  if (best.name === byPrice.name || withFreight(best) >= withFreight(byPrice))
    return null;
  return best.name;
}
